import { useState, useMemo, useCallback } from "react";
import { FilterType } from "@/components/test-management/TestToolbar";

interface TestLike {
  id: string;
  title: string;
  description: string;
  location: string;
  created_at: string;
  job_title?: string;
  job_description?: string;
  is_active?: boolean;
  status: string;
  response_count?: number;
  isCollaborated?: boolean;
}

export const useTestFilters = (tests: TestLike[]) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<FilterType[]>([]);

  const filteredTests = useMemo(() => {
    if (!tests) return [];

    let filtered = [...tests];

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((test) =>
        test.title.toLowerCase().includes(query) ||
        test.description.toLowerCase().includes(query) ||
        test.location.toLowerCase().includes(query) ||
        test.job_title?.toLowerCase().includes(query)
      );
    }

    // Apply filters
    if (activeFilters.length > 0) {
      filtered = filtered.filter((test) => {
        return activeFilters.every((filter) => {
          switch (filter) {
            case "owned":
              return !(test as any).isCollaborated;
            case "shared":
              return !!(test as any).isCollaborated;
            case "active":
              return test.is_active === true;
            case "inactive":
              return test.is_active === false;
            case "published":
              return test.status === "published";
            case "draft":
              return test.status === "draft";
            case "all":
            default:
              return true;
          }
        });
      });
    }

    return filtered;
  }, [tests, searchQuery, activeFilters]);

  const metrics = useMemo(() => {
    if (!tests) return { totalTests: 0, activeTests: 0, totalResponses: 0, sharedTests: 0 };

    const totalTests = tests.length;
    const activeTests = tests.filter(test => test.is_active === true).length;
    const totalResponses = tests.reduce((sum, test) => sum + (test.response_count || 0), 0);
    const sharedTests = tests.filter(test => (test as any).isCollaborated).length;

    return { totalTests, activeTests, totalResponses, sharedTests };
  }, [tests]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleFilterChange = useCallback((filters: FilterType[]) => {
    setActiveFilters(filters);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setActiveFilters([]);
  }, []);

  return {
    searchQuery,
    activeFilters,
    filteredTests,
    metrics,
    handleSearchChange,
    handleFilterChange,
    clearFilters
  };
};